import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getFrameworkMasterListApi } from '../../services/axios/AuthApis/TrustCenterApis';
import ApiLoader from '../ui/ApiLoader';
import { DEPARTMENTS_WITH_DESIGNATIONS } from '../../constants/AppConstants';

/* ── Constants ── */
const REVIEW_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-Annually', 'Annually', 'As Needed'];

/* ────────────────────────────────────────────────────────────────────────────
   AiLmsFormModal — Create / Edit an LMS training module
   Props:
     record      – null for create, existing record object for edit
     onClose     – closes the modal
     onSave(payload) – called with form data; should return a promise
   ──────────────────────────────────────────────────────────────────────────── */
const AiLmsFormModal = ({ record, onClose, onSave }) => {
  const isEdit = !!record;

  /* ── FrameworkMaster list (loaded internally) ── */
  const [frameworks, setFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFrameworksLoading(true);
      try {
        const res = await getFrameworkMasterListApi();
        const list = res?.frameworks || (Array.isArray(res) ? res : []);
        if (!cancelled) setFrameworks(list);
      } catch { /* ignore */ }
      finally { if (!cancelled) setFrameworksLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── form state ── */
  const [form, setForm] = useState({
    moduleTitle: record?.title || record?.moduleTitle || '',
    control_domain: record?.control_domain || '',
    category: record?.category || '',
    owner: record?.owner ? record.owner.split(' / ') : [],
    review_frequency: record?.review_frequency || '',
    description: record?.description || '',
    duration: record?.duration || '',
    serial_no: record?.serial_no || '',
    framework_ids: record?.frameworks?.map(fw => fw.id || fw) || [],
  });
  const [saving, setSaving] = useState(false);

  /* ── helpers ── */
  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  /* ── Framework helpers (multi-select) ── */
  const [frameworkSearch, setFrameworkSearch] = useState('');

  const filteredFrameworks = (frameworks || []).filter((fw) => {
    if (!frameworkSearch.trim()) return true;
    const q = frameworkSearch.toLowerCase();
    return (fw.name || '').toLowerCase().includes(q);
  });

  const toggleFramework = (fwId) => {
    setForm((f) => {
      const ids = f.framework_ids.includes(fwId)
        ? f.framework_ids.filter((id) => id !== fwId)
        : [...f.framework_ids, fwId];
      return { ...f, framework_ids: ids };
    });
  };

  /* ── Owner helpers (multi-select from departments/roles) ── */
  const [ownerSearch, setOwnerSearch] = useState('');

  const allOwnerRoles = DEPARTMENTS_WITH_DESIGNATIONS.flatMap((dept) =>
    dept.roles.map((r) => ({ key: r.role, role: r.role, department: dept.name, responsibility: r.responsibility }))
  );

  const filteredOwnerRoles = allOwnerRoles.filter((r) => {
    if (!ownerSearch.trim()) return true;
    const q = ownerSearch.toLowerCase();
    return r.role.toLowerCase().includes(q) || r.department.toLowerCase().includes(q);
  });

  const toggleOwner = (role) => {
    setForm((f) => {
      const owners = f.owner.includes(role)
        ? f.owner.filter((o) => o !== role)
        : [...f.owner, role];
      return { ...f, owner: owners };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.moduleTitle.trim()) return;
    if (!form.framework_ids || form.framework_ids.length === 0) return;
    setSaving(true);
    try {
      await onSave({ 
        ...form, 
        framework_ids: form.framework_ids,
        owner: form.owner.join(' / '),
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── style tokens ── */
  const inputCls =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500';
  const labelCls = 'block text-xs font-semibold text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-brand to-brand-dark px-6 py-4 flex items-center justify-between shrink-0">
          <h3 className="text-white font-bold text-lg">
            {isEdit ? 'Edit Training Module' : 'Add New Training Module'}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Loading overlay ── */}
        {(frameworksLoading || saving) && (
          <div className="flex-1 flex items-center justify-center py-16">
            <ApiLoader label={saving ? 'Saving…' : 'Loading…'} />
          </div>
        )}

        {/* ── Form body ── */}
        {!frameworksLoading && !saving && (
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Row 1 – Title */}
          <div>
            <label className={labelCls}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              className={inputCls}
              value={form.moduleTitle}
              onChange={handleChange('moduleTitle')}
              placeholder="Enter training module title"
              required
            />
          </div>

          {/* ── Framework Multi-Select ── */}
          <div>
            <label className={labelCls}>
              Framework <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal">({form.framework_ids.length} selected)</span>
            </label>

            {form.framework_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.framework_ids.map((fwId) => {
                  const fw = frameworks.find((f) => f.id === fwId);
                  return (
                    <span
                      key={fwId}
                      className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1"
                    >
                      <span className="font-semibold">{fw?.name || fwId}</span>
                      <button
                        type="button"
                        onClick={() => toggleFramework(fwId)}
                        className="ml-0.5 text-blue-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Search frameworks…"
                  value={frameworkSearch}
                  onChange={(e) => setFrameworkSearch(e.target.value)}
                  className="flex-1 text-xs bg-transparent outline-none"
                />
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                {filteredFrameworks.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">No matching frameworks.</p>
                ) : (
                  filteredFrameworks.map((fw) => {
                    const checked = form.framework_ids.includes(fw.id);
                    return (
                      <label
                        key={fw.id}
                        className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                          checked ? 'bg-blue-50/60' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFramework(fw.id)}
                          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-gray-800">{fw.name}</span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Review Frequency</label>
              <select className={inputCls} value={form.review_frequency} onChange={handleChange('review_frequency')}>
                <option value="">Select</option>
                {REVIEW_FREQUENCIES.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Serial No</label>
              <input
                type="number"
                className={inputCls}
                value={form.serial_no}
                onChange={handleChange('serial_no')}
              />
            </div>
            <div>
              <label className={labelCls}>Duration</label>
              <input
                type="text"
                className={inputCls}
                value={form.duration}
                onChange={handleChange('duration')}
                placeholder="e.g., 45 min"
              />
            </div>
          </div> */}

          {/* ── Owner multi-select ── */}
          <div>
            <label className={labelCls}>
              Owner{' '}
              <span className="text-gray-400 font-normal">({form.owner.length} selected)</span>
            </label>

            {/* Selected owners chips */}
            {form.owner.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.owner.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1"
                  >
                    <span className="font-semibold">{role}</span>
                    <button
                      type="button"
                      onClick={() => toggleOwner(role)}
                      className="ml-0.5 text-green-400 hover:text-red-500 transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Search departments or roles…"
                  value={ownerSearch}
                  onChange={(e) => setOwnerSearch(e.target.value)}
                  className="flex-1 text-xs bg-transparent outline-none"
                />
              </div>
              {/* Scrollable list */}
              <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                {filteredOwnerRoles.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">No matching roles.</p>
                ) : (
                  filteredOwnerRoles.map((r) => {
                    const checked = form.owner.includes(r.role);
                    return (
                      <label
                        key={`${r.department}-${r.role}`}
                        className={`flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors ${
                          checked ? 'bg-green-50/60' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOwner(r.role)}
                          className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-gray-800">{r.role}</span>
                          <span className="ml-2 text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {r.department}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Row 4 – textareas */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              rows={2}
              className={inputCls}
              value={form.description}
              onChange={handleChange('description')}
              placeholder="Enter module description"
            />
          </div>
        </form>
        )}

        {/* ── Footer ── */}
        {!frameworksLoading && !saving && (
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.moduleTitle.trim()}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {saving && (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {isEdit ? 'Update' : 'Create'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default AiLmsFormModal;
